import { TFunction } from "i18next";
import { z } from "zod";

import { FR_PHONE_REGEX, NAME_REGEX } from "src/core/forms/validators/regex";
import { parseDateInputAsLocalDate } from "src/core/utils/date";

/**
 * Options communes à toutes les règles de validation.
 */
type RuleOptions = {
  checkIfShown?: boolean;
  required?: boolean;
};

/**
 * Valide l'email.
 * Si checkIfShown est true, le contrôle sur l'email doit être effectué uniquement si le champ est affiché.
 */
export const emailRule = (t: TFunction, checkIfShown = false) =>
  z
    .string()
    .trim()
    .refine(
      (val) => {
        if (!checkIfShown) return true;
        if (!val) return true;
        return z.string().email().safeParse(val).success;
      },
      { message: t("global.form.error.invalidEmail") }
    )
    .optional();

/**
 * Valide le téléphone FR avec séparateurs usuels.
 * Si checkIfShown est true, le contrôle sur le téléphone doit être effectué uniquement si le champ est affiché.
 */
export const phoneRule = (t: TFunction, checkIfShown = false) =>
  z
    .string()
    .trim()
    .refine(
      (val) => {
        if (!checkIfShown) return true;
        if (!val) return true;
        return FR_PHONE_REGEX.test(val);
      },
      { message: t("global.form.error.invalidPhone") }
    );

/**
 * Valide un nom / prénom.
 * Si options.checkIfShown est true, la validation n'est faite que si le champ est affiché.
 * Si options.required est true, le champ est obligatoire.
 */
export const nameRule = (t: TFunction, options: RuleOptions = {}) => {
  const { checkIfShown = false, required = false } = options;

  let schema = z.string().trim();

  if (required) {
    schema = schema.min(1, {
      message: t("global.form.error.mandatoryField"),
    });
  }

  return schema.refine(
    (val) => {
      if (!checkIfShown) return true;
      if (!required && !val) return true;

      return NAME_REGEX.test(val);
    },
    { message: t("global.form.error.invalidName") }
  );
};

const MIN_AGE = 18;

const isAtLeastAge = (birthDate: Date, age: number, today = new Date()): boolean => {
  let years = today.getFullYear() - birthDate.getFullYear();

  const hasHadBirthdayThisYear =
    today.getMonth() > birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());

  if (!hasHadBirthdayThisYear) years -= 1;

  return years >= age;
};

export const dateOfBirthRule = (t: TFunction, options: RuleOptions = {}) => {
  const { checkIfShown = false, required = false } = options;

  let schema = z.string().trim();

  if (required) {
    schema = schema.min(1, {
      message: t("global.form.error.mandatoryField"),
    });
  }

  return schema.refine(
    (val) => {
      if (!checkIfShown) return true;
      if (!required && !val) return true;

      const birthDate = parseDateInputAsLocalDate(val);

      if (!birthDate) return false;

      return isAtLeastAge(birthDate, MIN_AGE);
    },
    { message: t("global.form.error.minorAge") }
  );
};
